import styled from 'styled-components';

import { IconButton } from '@components/icon-button';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconDuplicate } from '@components/icons/duplicate';
import { IconRatingGood } from '@components/icons/rating-good';
import { IconRatingPoor } from '@components/icons/rating-poor';
import { Stack } from '@components/stack';
import { useClipboard } from '@hooks/use-clipboard';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import type { MessageFeedback } from 'api/ai-builder/requests';

import { useDislikeFeedbackModal } from './dislike-feedback-modal';

type MessageActionsProps = {
  text: string;
  feedbackRating?: MessageFeedback | null;
  onFeedback: (feedback: MessageFeedback | null) => void;
  onSubmitDislikeFeedback?: (reasons: string[], details: string) => void;
};

export const MessageActions = ({
  text,
  feedbackRating,
  onFeedback,
  onSubmitDislikeFeedback,
}: MessageActionsProps) => {
  const { t } = useScopedTranslate(locale);
  const { onCopy, copied } = useClipboard({ delay: 2000 });
  const showDislikeFeedbackModal = useDislikeFeedbackModal();

  const handleCopy = () => {
    onCopy(text);
  };

  const handleLike = () => {
    onFeedback(feedbackRating === 'like' ? null : 'like');
  };

  const handleDislike = () => {
    if (feedbackRating === 'dislike') {
      onFeedback(null);
    } else {
      onFeedback('dislike');
      showDislikeFeedbackModal({
        onSubmit: (reasons, details) => {
          onSubmitDislikeFeedback?.(reasons, details);
        },
      });
    }
  };

  return (
    <Container>
      <IconButton
        variant="ghost-purple"
        size="sm"
        onClick={handleCopy}
        tooltip={copied ? t('copied') : t('copyResponse')}
        icon={
          copied ? (
            <IconCheckmark size={14} color="textSecondary" />
          ) : (
            <IconDuplicate size={14} color="textSecondary" />
          )
        }
      />
      {feedbackRating !== 'dislike' && (
        <IconButton
          variant="ghost-purple"
          size="sm"
          onClick={handleLike}
          tooltip={t('goodResponse')}
          icon={
            <IconRatingGood
              size={14}
              color={feedbackRating === 'like' ? 'elementTeal100' : 'textSecondary'}
            />
          }
        />
      )}
      {feedbackRating !== 'like' && (
        <IconButton
          variant="ghost-purple"
          size="sm"
          onClick={handleDislike}
          tooltip={t('badResponse')}
          icon={
            <IconRatingPoor
              size={14}
              color={feedbackRating === 'dislike' ? 'elementOrange100' : 'textSecondary'}
            />
          }
        />
      )}
    </Container>
  );
};

const Container = styled(Stack).attrs({
  orientation: 'horizontal',
  space: 0,
})({
  marginTop: pxToRem(4),
});

const locale = {
  en: {
    copyResponse: 'Copy response',
    copied: 'Copied',
    goodResponse: 'Good response',
    badResponse: 'Bad response',
  },
};
